/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import LoadingBar from "react-top-loading-bar";
import { useTranslation } from "react-i18next";

// @material-ui/core components
import { Tooltip } from "@mui/material";

// material-ui icons
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import Business from "@material-ui/icons/Business";

// core components
import Card from "components/UI/Card/Card.js";
import CardBody from "components/UI/Card/CardBody.js";
import CardHeader from "components/UI/Card/CardHeader.js";
import CardIcon from "components/UI/Card/CardIcon.js";
import GridContainer from "components/UI/Grid/GridContainer.js";
import FieldglassButton from "components/UI/FieldglassButton";
import ButtonTS from "components/Forms/CustomButtons/ButtonTS";

import { getBadgeFromStatus, getFilters, getSorting } from "utils/functions";
import Skeleton from "react-loading-skeleton";
import PaginatedTable from "components/Tables/ReactTable/ManualPaginatedTable";
import GridItemTS from "components/UI/Grid/GridItemTS";
import { useGetSuppliers } from "services/input-core/suppliers/get-suppliers-paginated";
import { TitlePages } from "components/UI/TitlePage/TitlePages";

export default function Suppliers() {
  const ref = useRef(null);
  const { t } = useTranslation("supplier-list");

  const [tableData, setTableData] = useState({
    pageSize: 10,
    pageIndex: 0,
    columnFilters: [],
    sorting: [],
  });

  const { data, isLoading, isFetching } = useGetSuppliers({
    "page[size]": tableData.pageSize,
    "page[index]": tableData.pageIndex,
    ...getFilters(tableData.columnFilters, true),
    ...getSorting(tableData.sorting),
  });

  const columns: ColumnDef<any, any>[] = [
    {
      footer: (props: any) => props.column.id,
      header: t("supplier-table-id"),
      accessorKey: "supplierCode",
    },
    {
      header: t("supplier-table-name"),
      footer: (props: any) => props.column.id,
      accessorKey: "supplierName",
    },
    {
      header: t("supplier-table-status"),
      footer: (props: any) => props.column.id,
      accessorKey: "status",
      cell: (info: any) =>
        getBadgeFromStatus(info.getValue(), (text: string) =>
          t(text, { ns: "status" }),
        ),
    },
    {
      header: t("supplier-table-action"),
      accessorKey: "actions",
      cell: ({ row }: any) => (
        <div className="actions-right">
          <Tooltip placement="top" title={t("go-to-supplier")}>
            <Link
              to={"/app/suppliers/profile?id=" + row?.original?.supplierCode}
            >
              <ButtonTS
                round
                justIcon
                size="sm"
                color="info"
                key={row.original.id}
              >
                <ArrowForwardIosIcon />
              </ButtonTS>
            </Link>
          </Tooltip>
        </div>
      ),
      enableColumnFilter: false,
    },
  ];

  return (
    <GridContainer>
      <LoadingBar color="#ff9800" ref={ref} />
      <GridItemTS xs={12}>
        <Card>
          <CardHeader color="rose" icon>
            <CardIcon color="rose">
              <Business />
            </CardIcon>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
              }}
            >
              <TitlePages type="sidebar" title="suppliers" />
              <FieldglassButton type="supplier" />
            </div>
          </CardHeader>
          <CardBody>
            {isLoading || isFetching ? (
              <Skeleton />
            ) : (
              <PaginatedTable
                id="suppliers-table"
                columns={columns}
                data={data?.records || []}
                tableState={tableData}
                pageCount={data?.page_count}
                setTableState={setTableData}
                fixedPageSize
              />
            )}
          </CardBody>
        </Card>
      </GridItemTS>
    </GridContainer>
  );
}
